import { useEffect, useState, useMemo } from "react";
import BoxAlert from '../../components/boxAlert/index.jsx';
import Header from "../../components/header/index.jsx";
import { Loading } from "../../components/loading/index.jsx";
import { getReviews } from "../../scripts/getReviews.js";
import ReviewsList from "./components/reviewsList/index.jsx";
import styles from "./css/reviews.module.css";
import { MdErrorOutline, MdOutlineInbox, MdOutlineRateReview, MdRefresh, MdSentimentDissatisfied, MdSentimentNeutral, MdSentimentSatisfiedAlt, MdFilterList, MdSearch, MdClose } from "react-icons/md";



// PERCENTUAL DE `part` SOBRE `total`. SEM AVALIAÇÕES (total = 0) NÃO HÁ PERCENTUAL, EM VEZ DE NaN
const toPercent = (part, total) => total > 0 ? (part * 100) / total : null;

const formatPercent = (value) => value === null ? '—' : `${value.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`;


// FILTROS DA LISTA: `match` DECIDE SE A AVALIAÇÃO ENTRA EM CADA OPÇÃO
const APP_FILTERS = [
  { value: 'all', label: 'Todos', match: () => true },
  { value: 'app', label: 'SofChef', match: (rev) => rev.app == 'app' },
  { value: 'cmv', label: 'CMV', match: (rev) => rev.app == 'cmv' },
];

const FEELING_FILTERS = [
  { value: 'all', label: 'Todas', match: () => true },
  { value: 'good', label: 'Boas', match: (rev) => rev.feeling == 'good' },
  { value: 'medium', label: 'Médias', match: (rev) => rev.feeling == 'medium' },
  { value: 'bad', label: 'Ruins', match: (rev) => rev.feeling == 'bad' },
];


// NORMALIZA O TEXTO PARA A BUSCA (SEM ACENTOS E SEM DIFERENCIAR MAIÚSCULAS/MINÚSCULAS)
const normalize = (text) => (text ?? '').toString().normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();


export default function Reviews() {

  const [reviews, setReviews] = useState([]);
  const [appFilter, setAppFilter] = useState('all');
  const [feelingFilter, setFeelingFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [warnAlert, setWarnAlert] = useState({ show: false, title: null, text: null });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);


  useEffect(() => {
    handleGetReviews();
  }, []);


  const cardsData = useMemo(() => {
    const total = reviews.length;

    const count = (feeling) => reviews.filter((rev) => rev.feeling == feeling).length;

    return {
      totalReviews: total,
      positive: toPercent(count('good'), total),
      neutral: toPercent(count('medium'), total),
      negative: toPercent(count('bad'), total),
    };
  }, [reviews])



  const activeApp = APP_FILTERS.find((filter) => filter.value == appFilter);
  const activeFeeling = FEELING_FILTERS.find((filter) => filter.value == feelingFilter);

  // AVALIAÇÕES QUE BATEM COM A BUSCA (NOME DO USUÁRIO OU DESCRIÇÃO). OS CONTADORES DOS CHIPS PARTEM DAQUI
  const searched = useMemo(() => {
    const term = normalize(search).trim();

    if (!term) return reviews;

    return reviews.filter((rev) => normalize(rev.name).includes(term) || normalize(rev.description).includes(term));
  }, [reviews, search]);

  // LISTA EXIBIDA: SEMPRE DERIVADA DAS AVALIAÇÕES, DA BUSCA E DOS FILTROS ATIVOS
  const reviewsRender = useMemo(
    () => searched.filter((rev) => activeApp.match(rev) && activeFeeling.match(rev)),
    [searched, appFilter, feelingFilter]
  );

  const hasActiveFilter = appFilter != 'all' || feelingFilter != 'all' || search.trim() != '';

  const clearFilters = () => {
    setAppFilter('all');
    setFeelingFilter('all');
    setSearch('');
  };


  const handleGetReviews = async () => {
    try{
      setLoading(true);
      setLoadError(false);

      const res = await getReviews();

      setReviews(res);

    } catch (e) {
      console.error('ERROR - INIT SCREEN:', e);
      setLoadError(true);

    } finally {
      setLoading(false);
    }
  }


  const summaryCards = [
    { key: 'good', title: 'Positivas', percent: cardsData.positive, Icon: MdSentimentSatisfiedAlt, className: styles.good },
    { key: 'medium', title: 'Neutras', percent: cardsData.neutral, Icon: MdSentimentNeutral, className: styles.medium },
    { key: 'bad', title: 'Negativas', percent: cardsData.negative, Icon: MdSentimentDissatisfied, className: styles.bad },
  ];



  // SCREEN ====================================>
  return (
    <div>
      <Header>
        <h2 className={styles.titleCompaniesArea} style={{ color: '#fff' }}>Avaliações</h2>
      </Header>

      <div className={styles.container}>
        <div className={styles.reviewsArea}>

          {/* RESUMO */}
          <div className={styles.cardsGrid}>
            <div className={styles.card}>
              <div className={`${styles.cardIcon} ${styles.total}`}>
                <MdOutlineRateReview size={22} />
              </div>

              <div className={styles.cardInfo}>
                <h2>Total de avaliações</h2>
                <p className={styles.value}>{cardsData.totalReviews}</p>
              </div>
            </div>

            {summaryCards.map(({ key, title, percent, Icon, className }) => (
              <div className={styles.card} key={key}>
                <div className={`${styles.cardIcon} ${className}`}>
                  <Icon size={22} />
                </div>

                <div className={styles.cardInfo}>
                  <h2>{title}</h2>
                  <p className={`${styles.value} ${percent === null ? styles.valueEmpty : ''}`}>{formatPercent(percent)}</p>

                  <div className={styles.progressTrack}>
                    <div className={`${styles.progressBar} ${className}`} style={{ width: `${percent ?? 0}%` }} />
                  </div>
                </div>
              </div>
            ))}
          </div>


          {/* FILTROS */}
          {!loading && !loadError && reviews.length > 0 && (
            <div className={styles.filtersArea}>
              <div className={styles.filtersHeader}>
                <h3><MdFilterList size={18} /> Filtrar avaliações</h3>

                {hasActiveFilter && (
                  <button className={styles.clearFilters} onClick={clearFilters}>Limpar filtros</button>
                )}
              </div>

              <div className={styles.searchBar}>
                <MdSearch size={20} className={styles.searchIcon} />

                <input
                  className={styles.searchInput}
                  type="text"
                  value={search}
                  placeholder="Buscar por nome do usuário ou descrição"
                  onChange={({ target: { value } }) => setSearch(value)}
                />

                {search != '' && (
                  <button className={styles.searchClear} onClick={() => setSearch('')} aria-label="Limpar busca">
                    <MdClose size={18} />
                  </button>
                )}
              </div>

              <div className={styles.filterGroup}>
                <span className={styles.filterLabel}>App</span>

                <div className={styles.chipsRow}>
                  {APP_FILTERS.map((filter) => {
                    const count = searched.filter((rev) => filter.match(rev) && activeFeeling.match(rev)).length;

                    return (
                      <button
                        key={filter.value}
                        className={`${styles.chip} ${appFilter == filter.value ? styles.chipActive : ''}`}
                        onClick={() => setAppFilter(filter.value)}
                      >
                        {filter.label} <span className={styles.chipCount}>{count}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className={styles.filterGroup}>
                <span className={styles.filterLabel}>Sentimento</span>

                <div className={styles.chipsRow}>
                  {FEELING_FILTERS.map((filter) => {
                    const count = searched.filter((rev) => filter.match(rev) && activeApp.match(rev)).length;

                    return (
                      <button
                        key={filter.value}
                        className={`${styles.chip} ${feelingFilter == filter.value ? styles.chipActive : ''}`}
                        onClick={() => setFeelingFilter(filter.value)}
                      >
                        {filter.label} <span className={styles.chipCount}>{count}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}


          {/* LISTA */}
          <div className={styles.reviewsTableArea}>

            <div className={styles.reviewsTileContainer}>
              <h1 className={styles.reviewsTitle}>Lista</h1>

              {!loading && !loadError && reviews.length > 0 && (
                <span className={styles.listCount}>
                  {reviewsRender.length == reviews.length ? `${reviews.length} avaliações` : `${reviewsRender.length} de ${reviews.length} avaliações`}
                </span>
              )}
            </div>

            {loading ? (
              <Loading />

            ) : loadError ? (
              <div className={styles.stateBox}>
                <div className={`${styles.stateIcon} ${styles.bad}`}><MdErrorOutline size={30} /></div>
                <h3>Não foi possível carregar as avaliações</h3>
                <p>Verifique a sua conexão de internet ou entre em contato com o TI para averiguar a situação.</p>

                <button className={styles.stateButton} onClick={handleGetReviews}>
                  <MdRefresh size={18} /> Tentar novamente
                </button>
              </div>

            ) : reviews.length == 0 ? (
              <div className={styles.stateBox}>
                <div className={`${styles.stateIcon} ${styles.total}`}><MdOutlineInbox size={30} /></div>
                <h3>Nenhuma avaliação ainda</h3>
                <p>Quando os usuários avaliarem o app, as avaliações aparecerão aqui.</p>
              </div>

            ) : reviewsRender.length == 0 ? (
              <div className={styles.stateBox}>
                <div className={`${styles.stateIcon} ${styles.total}`}><MdOutlineInbox size={30} /></div>
                <h3>Nenhuma avaliação encontrada</h3>
                <p>Nenhuma avaliação corresponde à busca e aos filtros aplicados.</p>

                <button className={styles.stateButton} onClick={clearFilters}>Limpar filtros</button>
              </div>

            ) : (
              <ReviewsList reviews={reviewsRender} />
            )}

          </div>
        </div>
      </div>



      {/* WARN ALERT */}

      <BoxAlert
        show={warnAlert.show}
        title={warnAlert.title}
        text={warnAlert.text}
        onClose={() => setWarnAlert({ show: false, title: null, text: null })}
      />

    </div>
  );
}
